/**
 * invoiceRules.js - the Finance Invoice tab's pure rules: what each status
 * looks like, the list's search and filters, its totals, the CSV export, and
 * where a row opens. The money rules themselves (status, balance, aging) are
 * the server's (server/lib/invoiceLedger.js) - the rows arrive with them.
 */
import { recordHash } from '../dashboard/lastWeek';

export const STATUSES = ['Open', 'Partial', 'Overdue', 'Paid', 'Needs review', 'Void'];
export const STATUS_STYLE = {
  Open: { bg: '#E3F2FD', color: '#0D47A1' },
  Partial: { bg: '#FFF8E1', color: '#8D6E00' },
  Overdue: { bg: '#FFEBEE', color: '#B71C1C' },
  Paid: { bg: '#E8F5E9', color: '#1B5E20' },
  'Needs review': { bg: '#FFF3E0', color: '#A04A00' },
  Void: { bg: '#F1F1F1', color: '#6B7280' },
};
export const BUCKETS = [
  ['current', 'Current'],
  ['d1_30', '1-30'],
  ['d31_60', '31-60'],
  ['d61_90', '61-90'],
  ['d90_plus', '90+'],
];

const cents = (n) => Math.round(n * 100) / 100;
const low = (v) => String(v ?? '').toLowerCase();

/** The project an invoice is for, as one line: "Q5101 · Paint line". */
export const projectLabel = (r) =>
  [r.quote_letter && `Q${r.quote_letter}`, r.project_name || r.name].filter(Boolean).join(' · ') || r.project_id || r.id || '';

/** An invoice's number as shown: the opening balance has none of its own. */
export const numberLabel = (r) => (r.kind === 'opening' ? 'Opening balance' : r.invoice_number || 'No #');

/** Where an invoice's project opens (the existing Projects / Opportunities deep links). */
export const projectHref = (r) => recordHash({ id: r.project_id, status: r.project_status || r.status });

/**
 * The rows that pass the filters: `q` (invoice #, customer, project, quote,
 * PO, notes), `status` ('' = all; 'unpaid' = Open + Partial + Overdue),
 * `customer`, `project`, and a `from` / `to` invoice-date range. PURE.
 */
export function filterInvoices(rows, { q = '', status = '', customer = '', project = '', from = '', to = '' } = {}) {
  const words = low(q).split(/\s+/).filter(Boolean);
  return (rows || []).filter((r) => {
    if (status === 'unpaid' ? !['Open', 'Partial', 'Overdue'].includes(r.status) : status && r.status !== status) return false;
    if (customer && (r.customer || '') !== customer) return false;
    if (project && String(r.project_id) !== String(project)) return false;
    if (from && !(r.invoice_date && r.invoice_date >= from)) return false;
    if (to && !(r.invoice_date && r.invoice_date <= to)) return false;
    if (!words.length) return true;
    const hay = low(
      [
        numberLabel(r),
        r.customer,
        r.project_name,
        r.quote_letter && `q${r.quote_letter}`,
        r.quote_letter,
        r.po_number,
        r.notes,
        r.status,
      ].join(' ')
    );
    return words.every((w) => hay.includes(w));
  });
}

/** { count, amount, paid, balance } of rows (void ones count for nothing). PURE. */
export function totalsOf(rows) {
  const live = (rows || []).filter((r) => r.status !== 'Void');
  return {
    count: live.length,
    amount: cents(live.reduce((s, r) => s + (Number(r.amount) || 0), 0)),
    paid: cents(live.reduce((s, r) => s + (Number(r.paid) || 0), 0)),
    balance: cents(live.reduce((s, r) => s + (r.balance === null || r.balance === undefined ? 0 : Number(r.balance)), 0)),
  };
}

/** The customers on the list, A-Z. */
export const customersOf = (rows) => [...new Set((rows || []).map((r) => r.customer).filter(Boolean))].sort((a, b) => a.localeCompare(b));

const two = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toFixed(2));
/** The list as CSV rows (exportCsv): one object per invoice, the columns in order. PURE. */
export function csvRows(rows) {
  return (rows || []).map((r) => ({
    'Invoice #': numberLabel(r),
    'Invoice date': r.invoice_date || '',
    'Due date': r.effective_due || r.due_date || '',
    Customer: r.customer || '',
    Project: r.project_name || '',
    'Quote #': r.quote_letter || '',
    'PO #': r.po_number || '',
    Amount: two(r.amount),
    Paid: two(r.paid),
    Balance: two(r.balance),
    Status: r.status || '',
    'Days past due': r.days_past_due || 0,
    Notes: r.notes || '',
    'Review note': r.needs_review ? r.review_note || '' : '',
    'Entered by': r.created_by || '',
    'Void reason': r.void_reason || '',
  }));
}

/** 'Net 30' style terms -> days, or '' (the server works the due date out the same way). */
export function netDaysOf(terms) {
  const s = String(terms || '').trim();
  const m = s.match(/net\s*-?\s*(\d{1,3})/i) || s.match(/^(\d{1,3})\s*(days?)?$/i);
  if (m) return Number(m[1]);
  if (/receipt|^cod$|immediate/i.test(s)) return 0;
  return '';
}

/** 'YYYY-MM-DD' plus n days. */
export const plusDays = (day, n) => new Date(Date.parse(`${day}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
